/**
 * SEO Utilities for Blog Pages
 * Handles meta tag injection, structured data markup, and SEO optimizations
 */

// Default SEO configuration
const DEFAULT_SEO = {
  siteName: 'TAK8 Car Rental',
  siteUrl: process.env.REACT_APP_SITE_URL || 'https://tak8.com.au',
  defaultTitle: 'TAK8 Car Rental - Perth Car Hire',
  defaultDescription: 'TAK8 offers flexible Perth car rental with hourly and long-term options. Enjoy simple booking, no hidden fees, and a wide range of vehicles.',
  defaultImage: '/img/tak8-og-image.jpg',
  twitterHandle: '@tak8rental'
};

/**
 * Update document title
 */
export const updateTitle = (title) => {
  document.title = title || DEFAULT_SEO.defaultTitle;
};

/**
 * Update or create meta tag
 */
const updateMetaTag = (selector, content) => {
  if (!content) return;
  
  let element = document.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    const [attr, value] = selector.replace(/[\[\]]/g, '').split('=');
    element.setAttribute(attr, value.replace(/"/g, ''));
    document.head.appendChild(element);
  }
  element.setAttribute('content', content);
};

/**
 * Update basic meta tags
 */
export const updateBasicMetaTags = ({ title, description, keywords, canonicalUrl }) => {
  // Update title
  updateTitle(title);
  
  // Update description
  updateMetaTag('meta[name="description"]', description || DEFAULT_SEO.defaultDescription);
  
  // Update keywords if provided
  if (keywords && keywords.length > 0) {
    updateMetaTag('meta[name="keywords"]', keywords.join(', '));
  }
  
  // Update canonical URL
  if (canonicalUrl) {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
  }
};

/**
 * Update Open Graph meta tags
 */
export const updateOpenGraphTags = ({ title, description, image, url, type = 'article' }) => {
  const ogTags = [
    { property: 'og:title', content: title || DEFAULT_SEO.defaultTitle },
    { property: 'og:description', content: description || DEFAULT_SEO.defaultDescription },
    { property: 'og:image', content: image || `${DEFAULT_SEO.siteUrl}${DEFAULT_SEO.defaultImage}` },
    { property: 'og:url', content: url || window.location.href },
    { property: 'og:type', content: type },
    { property: 'og:site_name', content: DEFAULT_SEO.siteName }
  ];

  ogTags.forEach(tag => {
    updateMetaTag(`meta[property="${tag.property}"]`, tag.content);
  });
};

/**
 * Update Twitter Card meta tags
 */
export const updateTwitterCardTags = ({ title, description, image }) => {
  const twitterTags = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: DEFAULT_SEO.twitterHandle },
    { name: 'twitter:title', content: title || DEFAULT_SEO.defaultTitle },
    { name: 'twitter:description', content: description || DEFAULT_SEO.defaultDescription },
    { name: 'twitter:image', content: image || `${DEFAULT_SEO.siteUrl}${DEFAULT_SEO.defaultImage}` }
  ];

  twitterTags.forEach(tag => {
    updateMetaTag(`meta[name="${tag.name}"]`, tag.content);
  });
};

/**
 * Generate structured data for blog post
 */
export const generateBlogPostStructuredData = (blogPost) => {
  if (!blogPost) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": blogPost.title,
    "description": blogPost.excerpt || blogPost.summary,
    "image": blogPost.featuredImage || blogPost.image,
    "author": {
      "@type": "Person",
      "name": blogPost.author?.name || "TAK8 Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": DEFAULT_SEO.siteName,
      "logo": {
        "@type": "ImageObject",
        "url": `${DEFAULT_SEO.siteUrl}/img/tak8-logo.png`
      }
    },
    "datePublished": blogPost.publishDate || blogPost.createdAt,
    "dateModified": blogPost.updatedAt || blogPost.publishDate || blogPost.createdAt,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${DEFAULT_SEO.siteUrl}/blog/${blogPost.slug}`
    },
    "url": `${DEFAULT_SEO.siteUrl}/blog/${blogPost.slug}`,
    "wordCount": blogPost.content ? blogPost.content.replace(/<[^>]*>/g, '').split(' ').length : 0,
    "timeRequired": `PT${blogPost.readingTime || blogPost.readTime || 5}M`,
    "keywords": blogPost.tags ? blogPost.tags.join(', ') : '',
    "articleSection": blogPost.categories && blogPost.categories.length > 0 
      ? (blogPost.categories[0].name || blogPost.categories[0]) 
      : (blogPost.category || 'General')
  };

  return structuredData;
};

/**
 * Generate structured data for blog list page
 */
export const generateBlogListStructuredData = (blogs) => {
  if (!blogs || blogs.length === 0) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": `${DEFAULT_SEO.siteName} Blog`,
    "description": "Travel guides, car rental tips, and local insights for Perth and Western Australia",
    "url": `${DEFAULT_SEO.siteUrl}/blog`,
    "publisher": {
      "@type": "Organization",
      "name": DEFAULT_SEO.siteName,
      "logo": {
        "@type": "ImageObject",
        "url": `${DEFAULT_SEO.siteUrl}/img/tak8-logo.png`
      }
    },
    "blogPost": blogs.map(blog => ({
      "@type": "BlogPosting",
      "headline": blog.title,
      "description": blog.excerpt || blog.summary,
      "image": blog.featuredImage || blog.image,
      "url": `${DEFAULT_SEO.siteUrl}/blog/${blog.slug}`,
      "datePublished": blog.publishDate || blog.createdAt,
      "author": {
        "@type": "Person",
        "name": blog.author?.name || "TAK8 Team"
      }
    }))
  };

  return structuredData;
};

/**
 * Inject structured data into page
 */
export const injectStructuredData = (structuredData) => {
  if (!structuredData) return;

  // Remove existing structured data
  const existingScript = document.querySelector('script[type="application/ld+json"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(structuredData);
  document.head.appendChild(script);
};

/**
 * Complete SEO setup for blog post
 */
export const setupBlogPostSEO = (blogPost) => {
  if (!blogPost) return;

  const seoData = {
    title: blogPost.seo?.metaTitle || `${blogPost.title} - ${DEFAULT_SEO.siteName}`,
    description: blogPost.seo?.metaDescription || blogPost.excerpt || blogPost.summary,
    keywords: blogPost.seo?.keywords || blogPost.tags,
    canonicalUrl: blogPost.seo?.canonicalUrl || `${DEFAULT_SEO.siteUrl}/blog/${blogPost.slug}`,
    image: blogPost.seo?.openGraph?.image || blogPost.featuredImage || blogPost.image,
    url: `${DEFAULT_SEO.siteUrl}/blog/${blogPost.slug}`
  };

  // Update basic meta tags
  updateBasicMetaTags(seoData);

  // Update Open Graph tags
  updateOpenGraphTags({
    title: blogPost.seo?.openGraph?.title || seoData.title,
    description: blogPost.seo?.openGraph?.description || seoData.description,
    image: seoData.image,
    url: seoData.url
  });

  // Update Twitter Card tags
  updateTwitterCardTags({
    title: blogPost.seo?.twitterCard?.title || seoData.title,
    description: blogPost.seo?.twitterCard?.description || seoData.description,
    image: blogPost.seo?.twitterCard?.image || seoData.image
  });

  // Generate and inject structured data
  const structuredData = generateBlogPostStructuredData(blogPost);
  injectStructuredData(structuredData);
};

/**
 * Complete SEO setup for blog list page
 */
export const setupBlogListSEO = (blogs, pageTitle, pageDescription) => {
  const seoData = {
    title: pageTitle || `Travel Blog - ${DEFAULT_SEO.siteName}`,
    description: pageDescription || 'Discover Perth and Western Australia with TAK8\'s travel blog. Expert travel guides, car rental tips, and local insights for your perfect journey.',
    canonicalUrl: `${DEFAULT_SEO.siteUrl}/blog`,
    url: `${DEFAULT_SEO.siteUrl}/blog`
  };

  // Update basic meta tags
  updateBasicMetaTags(seoData);

  // Update Open Graph tags
  updateOpenGraphTags({
    title: seoData.title,
    description: seoData.description,
    url: seoData.url,
    type: 'website'
  });

  // Update Twitter Card tags
  updateTwitterCardTags({
    title: seoData.title,
    description: seoData.description
  });

  // Generate and inject structured data
  const structuredData = generateBlogListStructuredData(blogs);
  injectStructuredData(structuredData);
};

/**
 * Setup SEO for category pages
 */
export const setupCategorySEO = (category, blogs) => {
  const categoryName = typeof category === 'string' ? category : category?.name || 'Category';
  const seoData = {
    title: `${categoryName} Articles - ${DEFAULT_SEO.siteName} Blog`,
    description: `Explore ${categoryName.toLowerCase()} articles and guides from TAK8. Find expert tips and insights for your Perth travel experience.`,
    canonicalUrl: `${DEFAULT_SEO.siteUrl}/blog/category/${typeof category === 'string' ? category : category?.slug}`,
    url: `${DEFAULT_SEO.siteUrl}/blog/category/${typeof category === 'string' ? category : category?.slug}`
  };

  setupBlogListSEO(blogs, seoData.title, seoData.description);
};

/**
 * Setup SEO for tag pages
 */
export const setupTagSEO = (tag, blogs) => {
  const seoData = {
    title: `${tag} Articles - ${DEFAULT_SEO.siteName} Blog`,
    description: `Find all articles tagged with "${tag}" on TAK8 blog. Discover related content and expert insights.`,
    canonicalUrl: `${DEFAULT_SEO.siteUrl}/blog/tag/${tag}`,
    url: `${DEFAULT_SEO.siteUrl}/blog/tag/${tag}`
  };

  setupBlogListSEO(blogs, seoData.title, seoData.description);
};

/**
 * Setup SEO for search results
 */
export const setupSearchSEO = (query, blogs) => {
  const seoData = {
    title: `Search Results for "${query}" - ${DEFAULT_SEO.siteName} Blog`,
    description: `Search results for "${query}" on TAK8 blog. Find relevant articles and travel guides.`,
    canonicalUrl: `${DEFAULT_SEO.siteUrl}/blog/search?q=${encodeURIComponent(query)}`,
    url: `${DEFAULT_SEO.siteUrl}/blog/search?q=${encodeURIComponent(query)}`
  };

  setupBlogListSEO(blogs, seoData.title, seoData.description);
};

/**
 * Clean up SEO tags when component unmounts
 */
export const cleanupSEO = () => {
  // Remove structured data
  const structuredDataScript = document.querySelector('script[type="application/ld+json"]');
  if (structuredDataScript) {
    structuredDataScript.remove();
  }

  // Reset to default title
  document.title = DEFAULT_SEO.defaultTitle;
  
  // Reset meta description
  updateMetaTag('meta[name="description"]', DEFAULT_SEO.defaultDescription);
};

export default {
  updateTitle,
  updateBasicMetaTags,
  updateOpenGraphTags,
  updateTwitterCardTags,
  generateBlogPostStructuredData,
  generateBlogListStructuredData,
  injectStructuredData,
  setupBlogPostSEO,
  setupBlogListSEO,
  setupCategorySEO,
  setupTagSEO,
  setupSearchSEO,
  cleanupSEO
};